import React, { useState, useEffect, useRef } from 'react';
import Form from '../../components/Form/Form';
import MasterCreationFooter from '../../components/Footer/Master_Footer/MasterCreationFooter';
import { toast } from 'react-toastify';

function BranchCreateModal({ closeModal_sec, onSuccess }) {
    const [submitConfirm, setSubmitConfirm] = useState('');
    const [loading, setLoading] = useState(false);

    // Initial state for form fields matching screenshot
    const [formData, setFormData] = useState({
        branchName: 'Pachore Road',
        branchCode: 'Auto Generated',
        branchManager: 'Kamal Kushwah (Ac No)',
        under: 'Main Location',
        branchStatus: 'Active',
        allowedBackDated: 'Yes',
        cutOffDate: '23-Dec-2025 (Tuesday)',
        mailingName: 'Yadav Krishi Sewa Kendra',
        mailingAddress1: 'Pachore Road, Near Police Station',
        mailingAddress2: 'Boda',
        mailingState: 'Madhya Pradesh',
        mailingPincode: '465685',
        mailingMobile: '91 9617965916',
        mailingEmail: 'snlyadav11@gmail.com'
    });

    const [errors, setErrors] = useState({});

    // Focus references
    const refs = {
        branchName: useRef(null),
        branchManager: useRef(null),
        under: useRef(null),
        branchStatus: useRef(null),
        allowedBackDated: useRef(null),
        cutOffDate: useRef(null),
        mailingName: useRef(null),
        mailingAddress1: useRef(null),
        mailingAddress2: useRef(null),
        mailingState: useRef(null),
        mailingPincode: useRef(null),
        mailingMobile: useRef(null),
        mailingEmail: useRef(null)
    };

    // Sequential inputs order for Enter key navigation
    const inputOrder = [
        'branchName', 'branchManager', 'under', 'branchStatus', 'allowedBackDated', 'cutOffDate',
        'mailingName', 'mailingAddress1', 'mailingAddress2', 'mailingState', 'mailingPincode', 'mailingMobile', 'mailingEmail'
    ];

    useEffect(() => {
        // Block global Escape navigation while this modal is open
        document.body.setAttribute('data-modal-open', 'true');
        // Auto focus the first field on mount
        setTimeout(() => {
            refs.branchName.current?.focus();
            refs.branchName.current?.select();
        }, 100);
        return () => {
            document.body.removeAttribute('data-modal-open');
        };
    }, []);

    // Global Escape key listener
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setSubmitConfirm(prev => {
                    if (prev === 'Accept' || prev === 'Quit') return prev;
                    return 'Quit';
                });
            }
        };
        document.addEventListener('keydown', handleEscapeKey, true);
        return () => document.removeEventListener('keydown', handleEscapeKey, true);
    }, []);

    const validateField = (name, value) => {
        let error = '';
        if (name === 'branchName' && !value.trim()) {
            error = 'Branch Name is required';
        } else if (name === 'mailingPincode' && value && !/^\d{6}$/.test(value)) {
            error = 'Invalid pincode (should be 6 digits)';
        } else if (name === 'mailingEmail' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = 'Invalid email address';
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleFormKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const currentIndex = inputOrder.indexOf(e.target.name);
            if (currentIndex !== -1 && currentIndex < inputOrder.length - 1) {
                const nextFieldName = inputOrder[currentIndex + 1];
                refs[nextFieldName].current?.focus();
                if (refs[nextFieldName].current?.select) {
                    refs[nextFieldName].current?.select();
                }
            } else if (currentIndex === inputOrder.length - 1) {
                // Validate all fields before triggering Accept
                let hasErrors = false;
                const newErrors = {};
                inputOrder.forEach(field => {
                    const err = validateField(field, formData[field]);
                    if (err) {
                        hasErrors = true;
                        newErrors[field] = err;
                    }
                });
                if (hasErrors) {
                    setErrors(newErrors);
                    toast.error("Please correct the errors in the form first.");
                } else {
                    setSubmitConfirm('Accept');
                }
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setSubmitConfirm('Quit');
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        // Quit confirmation
        if (e && e.type === 'quit') {
            setSubmitConfirm('');
            closeModal_sec && closeModal_sec();
            return;
        }
        // Accept confirmation
        if (!submitConfirm) {
            let hasErrors = false;
            const newErrors = {};
            inputOrder.forEach(field => {
                const err = validateField(field, formData[field]);
                if (err) {
                    hasErrors = true;
                    newErrors[field] = err;
                }
            });
            if (hasErrors) {
                setErrors(newErrors);
                toast.error("Please correct the errors in the form first.");
                return;
            }
            setSubmitConfirm('Accept');
            return;
        }
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            toast.success("Branch created successfully!", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 1500
            });
            if (onSuccess) {
                // Generate a branch code based on name or randomly if not matches defaults
                let code = '99';
                if (formData.branchName.toLowerCase().includes('road')) code = '11';
                else if (formData.branchName.toLowerCase().includes('pachore')) code = '74';
                else code = String(Math.floor(Math.random() * 90) + 10);

                onSuccess({
                    name: formData.branchName,
                    branch_code: code,
                    address: formData.mailingAddress2 || 'N/A',
                    under: formData.under,
                    branch_manager: formData.branchManager.split(' (')[0],
                    mobile: formData.mailingMobile.replace('91 ', ''),
                    status: formData.branchStatus
                });
            }
            closeModal_sec && closeModal_sec();
        }, 800);
    };

    const handleFocusReset = () => {
        setSubmitConfirm('');
        refs.branchName.current?.focus();
    };

    // Bottom Grid Data from screenshot
    const gridData = [
        { godown: 'Main Location', voucher: 'Contra', user: 'Kamal Kushwah (kamal456) Parsukhedi' },
        { godown: 'Pachore Road - Boda', voucher: 'Credit Note', user: 'Sunil Yadav (snlyadav11) Boda' },
        { godown: 'Pachore', voucher: 'Debit Note', user: '' },
        { godown: '', voucher: 'Delivery Note', user: '' },
        { godown: '', voucher: 'Journal', user: '' },
        { godown: '', voucher: 'Payment', user: '' },
        { godown: '', voucher: 'Physical Stock', user: '' },
        { godown: '', voucher: 'Purchase', user: '' },
        { godown: '', voucher: 'Purchase Order', user: '' },
        { godown: '', voucher: 'Receipt', user: '' },
        { godown: '', voucher: 'Receipt Note', user: '' },
        { godown: '', voucher: 'Sales', user: '' },
        { godown: '', voucher: 'Sales Order', user: '' },
        { godown: '', voucher: 'Stock Transfer', user: '' },
    ];

    const managers = ['Kamal Kushwah (Ac No)', 'Amar Dubey (Ac No)', 'Sunil Yadav (Ac No)', 'Deepak Rathore (Ac No)'];
    const unders = ['Main Location', 'Primary'];
    const statuses = ['Active', 'Inactive'];
    const yesNoOptions = ['Yes', 'No'];

    return (
        <Form
            handleSubmit={handleSubmit}
            handleKeyDown={handleFormKeyDown}
            submitConfirm={submitConfirm}
            handleFocusReset={handleFocusReset}
            title='Branch Creation'
            loading={loading}
            col='col-12'
            Footer={MasterCreationFooter}
            closeModal_sec={closeModal_sec}
        >
            <div className="container-fluid p-2" style={{ background: '#f8f9fa', fontSize: '11px', display: 'flex', flexDirection: 'column', maxHeight: '82vh' }}>
                
                {/* Main Two-Column Layout */}
                <div className="row g-2 mb-2 bg-white p-3 border rounded shadow-sm m-0">
                    
                    {/* Left Column - Branch Settings */}
                    <div className="col-md-6 border-end pe-3">
                        <div className="row align-items-center mb-1">
                            <div className="col-5 fw-bold text-start">Branch Name</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6">
                                <input
                                    type="text"
                                    name="branchName"
                                    className={`form-control form-control-sm border-secondary fw-bold text-dark ${errors.branchName ? 'is-invalid' : ''}`}
                                    value={formData.branchName}
                                    onChange={handleChange}
                                    ref={refs.branchName}
                                    required
                                    style={{ fontSize: '11px' }}
                                />
                                {errors.branchName && <div className="invalid-feedback text-start" style={{ fontSize: '9px' }}>{errors.branchName}</div>}
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-5 fw-bold text-start">Branch Code</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6 text-start text-muted fw-bold ps-3">
                                {formData.branchCode}
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-5 fw-bold text-start">Branch Manager (Ac No.)</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6">
                                <select
                                    name="branchManager"
                                    className="form-select form-select-sm border-secondary fw-bold text-dark"
                                    value={formData.branchManager}
                                    onChange={handleChange}
                                    ref={refs.branchManager}
                                    style={{ fontSize: '11px' }}
                                >
                                    {managers.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-5 fw-bold text-start">Under</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6">
                                <select
                                    name="under"
                                    className="form-select form-select-sm border-secondary fw-bold text-dark"
                                    value={formData.under}
                                    onChange={handleChange}
                                    ref={refs.under}
                                    style={{ fontSize: '11px' }}
                                >
                                    {unders.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="row align-items-center mb-1 mt-3">
                            <div className="col-5 fw-bold text-start">Branch Status</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6">
                                <select
                                    name="branchStatus"
                                    className="form-select form-select-sm border-secondary fw-bold text-dark"
                                    value={formData.branchStatus}
                                    onChange={handleChange}
                                    ref={refs.branchStatus}
                                    style={{ fontSize: '11px' }}
                                >
                                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-5 fw-bold text-start">Allowed for Back Dated Vouchers</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6">
                                <select
                                    name="allowedBackDated"
                                    className="form-select form-select-sm border-secondary fw-bold text-dark"
                                    value={formData.allowedBackDated}
                                    onChange={handleChange}
                                    ref={refs.allowedBackDated}
                                    style={{ fontSize: '11px' }}
                                >
                                    {yesNoOptions.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-5 fw-bold text-start">Cut-Off Date for Back Dated Vouchers</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-6">
                                <input
                                    type="text"
                                    name="cutOffDate"
                                    className="form-control form-control-sm border-secondary fw-bold text-dark"
                                    value={formData.cutOffDate}
                                    onChange={handleChange}
                                    ref={refs.cutOffDate}
                                    style={{ fontSize: '11px' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Mailing Details */}
                    <div className="col-md-6 ps-3">
                        <div className="row mb-2">
                            <div className="col text-start fw-bold text-secondary text-uppercase border-bottom pb-1" style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}>
                                Mailing Details
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-4 fw-bold text-start">Name</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-7">
                                <input
                                    type="text"
                                    name="mailingName"
                                    className="form-control form-control-sm border-secondary fw-bold text-dark"
                                    value={formData.mailingName}
                                    onChange={handleChange}
                                    ref={refs.mailingName}
                                    style={{ fontSize: '11px' }}
                                />
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-4 fw-bold text-start">Address</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-7">
                                <input
                                    type="text"
                                    name="mailingAddress1"
                                    className="form-control form-control-sm border-secondary fw-bold text-dark mb-1"
                                    value={formData.mailingAddress1}
                                    onChange={handleChange}
                                    ref={refs.mailingAddress1}
                                    style={{ fontSize: '11px' }}
                                    placeholder="Line 1"
                                />
                                <input
                                    type="text"
                                    name="mailingAddress2"
                                    className="form-control form-control-sm border-secondary fw-bold text-dark"
                                    value={formData.mailingAddress2}
                                    onChange={handleChange}
                                    ref={refs.mailingAddress2}
                                    style={{ fontSize: '11px' }}
                                    placeholder="Line 2"
                                />
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-4 fw-bold text-start">State</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-7">
                                <input
                                    type="text"
                                    name="mailingState"
                                    className="form-control form-control-sm border-secondary fw-bold text-dark"
                                    value={formData.mailingState}
                                    onChange={handleChange}
                                    ref={refs.mailingState}
                                    style={{ fontSize: '11px' }}
                                />
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-4 fw-bold text-start">Pincode</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-7">
                                <input
                                    type="text"
                                    name="mailingPincode"
                                    className={`form-control form-control-sm border-secondary fw-bold text-dark ${errors.mailingPincode ? 'is-invalid' : ''}`}
                                    value={formData.mailingPincode}
                                    onChange={handleChange}
                                    ref={refs.mailingPincode}
                                    style={{ fontSize: '11px' }}
                                />
                                {errors.mailingPincode && <div className="invalid-feedback text-start" style={{ fontSize: '9px' }}>{errors.mailingPincode}</div>}
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-4 fw-bold text-start">Primary Mobile No.</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-7">
                                <input
                                    type="text"
                                    name="mailingMobile"
                                    className="form-control form-control-sm border-secondary fw-bold text-dark"
                                    value={formData.mailingMobile}
                                    onChange={handleChange}
                                    ref={refs.mailingMobile}
                                    style={{ fontSize: '11px' }}
                                />
                            </div>
                        </div>

                        <div className="row align-items-center mb-1">
                            <div className="col-4 fw-bold text-start">Email</div>
                            <div className="col-1 text-center">:</div>
                            <div className="col-7">
                                <input
                                    type="email"
                                    name="mailingEmail"
                                    className={`form-control form-control-sm border-secondary fw-bold text-dark ${errors.mailingEmail ? 'is-invalid' : ''}`}
                                    value={formData.mailingEmail}
                                    onChange={handleChange}
                                    ref={refs.mailingEmail}
                                    style={{ fontSize: '11px' }}
                                />
                                {errors.mailingEmail && <div className="invalid-feedback text-start" style={{ fontSize: '9px' }}>{errors.mailingEmail}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Section - Lists Grid */}
                <div className="table-responsive bg-white rounded border shadow-sm m-0 p-0" style={{ flexGrow: 1, overflowY: 'auto', maxHeight: '35vh' }}>
                    <table className="table table-bordered table-sm text-center m-0 align-middle" style={{ fontSize: '11px' }}>
                        <thead>
                            <tr className="table-secondary align-middle text-uppercase fw-bold text-secondary" style={{ fontSize: '11px', background: '#f1f3f5' }}>
                                <th className="text-start ps-3" style={{ width: '30%', height: '24px' }}>GODOWN</th>
                                <th className="text-start ps-3" style={{ width: '35%' }}>VOUCHER</th>
                                <th className="text-start ps-3" style={{ width: '35%' }}>USER (Username) City</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((item, idx) => {
                                return (
                                    <tr key={idx} className="align-middle">
                                        <td className="text-start ps-3 fw-bold text-dark" style={{ height: '22px' }}>{item.godown}</td>
                                        <td className="text-start ps-3 text-muted fw-bold">{item.voucher}</td>
                                        <td className="text-start ps-3 fw-bold text-dark">{item.user}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

            </div>
        </Form>
    );
}

export default React.memo(BranchCreateModal);
